import { useEffect, useState } from 'react';
import './StoriesLibrary.scss';
import { alert as byzzerAlert, openModal } from '@/components/form';
import {useTenantApi} from '@/hooks/useTenantApi';
import { storySelectorAttributes } from '@/utils/storySelectorUtil';
import { getDefaultImageSrc, snakeCaseToTitleCase, useBetterNavigate } from '@/utils/utils';
import className from 'classnames';
import { TrackClick } from '@/analytics';
import { ByzzerMask } from '@/components/ByzzerMask/ByzzerMask';
import { initializeStoredValues, getAccessLevel } from '@/utils/storiesUtil';
import { useApp, useUser, useEvents } from '@/contexts/UserContext';
import { showErrorModal, toRunsText } from '@/utils';
import { storyToHide, alertMessages, eventTypes, storySkus } from '@/config/globalVars';
import { SubscriptionInfo } from "@/constants/subscription.constants";
import { useStoryRunService } from '@/services/storiesRun.service';
import { ByzzerCard } from '@/components/ByzzerCard';
import { Link } from 'react-router-dom';

const wrapperClass = className('stories-library');
// Needs Refactoring to be done in sprint 1.64
function StoryLibrary() {
    const { getMySubscriptionUsage } = useTenantApi();
    const navigate = useBetterNavigate();
    const events = useEvents();
    const { allProducts } = useApp();
    const { getStoryDefaultRunConfigBySku } = useStoryRunService();
    const { subscription, user, subscribedSkus, company } = useUser();
    const [loading, setLoading] = useState(false);
    const [stories, setStories] = useState<ProductWithMeta[]>([]);
    const imageBucketURL = import.meta.env.REACT_APP_IMAGES_BASE_URL;
    const [remainingCoreRuns, setRemainingCoreRuns] = useState<number>(0);
    const [remainingSmartRuns, setRemainingSmartRuns] = useState<number>(0);
    const userAccessLevel = parseInt(getAccessLevel(company?.accessLevel) ?? '0');
    //TODO: This variable is just a placeholder this variable will be replaced by storyConfig(albyConfig) attributes thubnail lable BYZ-7160
    const storySkusForTags = [storySkus.categoryManagement];
    const StoryType = "Core Advanced";
    useEffect(() => {
        loadStorySubscriptionUsage();
        initializeStoredValues();
    }, []);

    useEffect(() => {
        loadStorySubscription();
    }, [allProducts, subscription]);

    useEffect(() => {
        const [event] = events;
        if (event?.type === eventTypes.storyGenerated) {
            loadStorySubscriptionUsage();
        }
    }, [events]);

    const loadStorySubscription = async () => {
        if (allProducts) {
            const products = allProducts.filter(isValidStory);
            products.forEach((elem) => {
                if (!elem?.thumbnailUrl?.includes(imageBucketURL, 0)) {
                    elem.thumbnailUrl = imageBucketURL + elem?.thumbnailUrl;
                }
            });
            setStories(products);
        }
    };

    async function loadStorySubscriptionUsage(showLoadingIndicator = true) {
        try {
            if (showLoadingIndicator) setLoading(true);
            const [{basicReports, premiumReports}] = await Promise.all([getMySubscriptionUsage()]);
            setRemainingCoreRuns(basicReports ? basicReports.limit - basicReports.used : 0);
            setRemainingSmartRuns(premiumReports ? premiumReports.limit - premiumReports.used : 0);
        } catch (err) {
            showErrorModal(err);
        } finally {
            setLoading(false);
        }
    }

    const isAccessibleStory = (storyProduct: typeof allProducts[number]) => {
        return company?.accessLevel ? storyProduct.accessLevels.includes(company.accessLevel) : true;
    }

    const isValidStory = (storyProduct: typeof allProducts[number]) => {
        // In Progress
        if ((storyToHide?.includes(storyProduct?.sku) && 2 <= userAccessLevel && userAccessLevel <= 4) || !isAccessibleStory(storyProduct)) {
            return false; // brand review story not to be shown to user
        } else
            return (
                storyProduct?.type === 'story' &&
                Number.isFinite(storyProduct?.subscriptionLevel) &&
                storyProduct?.sku &&
                storyProduct?.title !== '' &&
                storyProduct?.metadata?.env?.reportRunValue &&
                storyProduct?.metadata?.env?.reportSelectors &&
                storyProduct?.metadata?.env?.objective_lists &&
                storyProduct?.metadata?.env?.reportSelectors?.length !== 0 &&
                Object.keys(storyProduct?.metadata?.env?.objective_lists).length !== 0
            );
    };

    const validateStoryCountAndShow = async (storyConfig) => {
        if (user?.role === 'viewer') {
            openModal({
                closeOnClickOutside: true,
                title: "Sorry! You don't have access to do that.",
                content: (
                    <>
                        <p>As a viewer, you don't have access to run a new story. </p>
                        <p>Reach out to your account admin to upgrade your member type to user.</p>
                    </>
                ),
                className: `${wrapperClass}__viewer-access-modal`,
                headerType: 'normal',
            });
            return;
        }

        let subscribed = subscribedSkus.includes(storyConfig.sku);

        if (subscribed) {
            setLoading(true);

            let [usage] = await Promise.all([getMySubscriptionUsage()]);

            if (userAccessLevel === 5) {
                storyConfig.metadata.env.reportSelectors = storyConfig.metadata?.env?.reportSelectors.filter(
                    (e) => e !== 'market:option5'
                );
            }

            storyConfig.reportDetails = {
                reportType: storyConfig?.type,
                reportSelectors: storyConfig.metadata?.env?.reportSelectors
                    ? storySelectorAttributes(storyConfig?.metadata?.env?.reportSelectors)
                    : {},
                productSku: storyConfig?.sku,
                storyType: storyConfig.metadata?.env?.storyType,
                reportRunValue: storyConfig.metadata?.env?.reportRunValue,
            };

            if (storyConfig?.type === 'story' && usage) {
                if (usage?.basicReports?.limit === 0) {
                    setLoading(false);
                    byzzerAlert({
                        // @ts-expect-error: onResolve does exist but is not defined in the type.  todo: update type
                        onResolve: () => { },
                        content: (
                            <>
                                <p>{alertMessages.noTeamRunsLeft}</p>
                            </>
                        ),
                    });
                } else if (usage?.basicReports?.limit - usage?.basicReports?.used <= 0) {
                    setLoading(false);
                    byzzerAlert({
                        // @ts-expect-error: onResolve does exist but is not defined in the type.  todo: update type
                        onResolve: () => { },
                        content: (
                            <>
                                <p>{alertMessages.usedAllPurchasedCredits}</p>
                            </>
                        ),
                    });
                } else if (
                    usage?.basicReports?.limit - usage?.basicReports?.used - storyConfig.metadata?.env?.reportRunValue <
                    0
                ) {
                    setLoading(false);
                    byzzerAlert({
                        // @ts-expect-error: onResolve does exist but is not defined in the type.  todo: update type
                        onResolve: () => { },
                        content: (
                            <>
                                <p>{alertMessages.upgradeSubscription}</p>
                            </>
                        ),
                    });
                }
                if (
                    usage?.basicReports?.limit - usage?.basicReports?.used > 0 &&
                    usage?.basicReports?.limit -
                    usage?.basicReports?.used -
                    storyConfig?.metadata?.env?.reportRunValue >=
                    0
                ) {
                    localStorage.setItem('storySku', storyConfig?.sku);
                    localStorage.setItem('dataSpecificationValues', '{}');
                    localStorage.removeItem('selectorsConfigurations');
                    localStorage.removeItem('selectedObjectives');

                    navigate('/dashboard/story_builder', {
                        params: {},
                        state: {
                            storyConfig: {
                                ...storyConfig,
                            }, //will need change later 10-19-20 to pass all data, not conditionally pass only some.
                            defaultValues: getStoryDefaultRunConfigBySku(storyConfig?.sku),
                            remainingReportRuns: remainingCoreRuns,
                        },
                    });
                }
            }
            setLoading(false);
        }
    };


    function renderUsage() {
        return (
            <header className="my-stories__sub-heading">
                <div>
                    {!(subscription?.active) ? (
                        <p>
                            {SubscriptionInfo.SUBSCRITPION_EXPIRED}
                        </p>
                    ) : (
                        remainingCoreRuns === Infinity ? (
                            <div>
                                You have <span className="stories-report-count-text">unlimited core report runs</span> to
                                use for stories.
                            </div>
                        ) : (
                            <div>
                                You have{' '}
                                <span className="stories-report-count-text">
                                    {remainingCoreRuns > 0 ? remainingCoreRuns : 'no'} core report run
                                    {remainingCoreRuns === 1 ? '' : 's'}
                                </span>{' '}and{' '}
                                <span className="stories-report-count-text">
                                    {toRunsText(remainingSmartRuns)} smart report
                                    run{remainingSmartRuns === 1 ? '' : 's'}{' '}
                                    left{' '}
                                </span>
                                in your subscription.{' '}
                            </div>
                        )
                    )}
                </div>
            </header>
        );
    }
 
    async function validateStory(storyConfig) {
        if (user?.role === 'viewer') {
            return false;
        }
        
        let subscribed = subscribedSkus.includes(storyConfig.sku);

        if (subscribed) {
            let [usage] = await Promise.all([getMySubscriptionUsage()]);

            if (userAccessLevel === 5) {
                storyConfig.metadata.env.reportSelectors = storyConfig.metadata?.env?.reportSelectors.filter(
                    (e) => e !== 'market:option5'
                );
            }

            if (storyConfig?.type === 'story' && usage) {
                if (
                    usage?.basicReports?.limit - usage?.basicReports?.used > 0 &&
                    usage?.basicReports?.limit -
                    usage?.basicReports?.used -
                    storyConfig?.metadata?.env?.reportRunValue >=
                    0
                ) {
                    return true;
                }
            }
        }

        return false;
    }

    return (
        <>
            <ByzzerMask loading={loading} />

            {stories?.length > 0 && <>{renderUsage()}</>}

            {/* STORIES LIST VIEW   */}
            <div className="subscription-reports ad-hoc-reports--runnable subscription-story">
                <div className="subscription-reports__content">
                    <div className="story-groups">
                        <div className={'report-group'}>
                            <div className="report-group__cards">
                                {Boolean(stories.length) &&
                                    stories.map((item, index) => {
                                        return (
                                            <StoriesCard key={index} story={item} validateStory={validateStory} validateStoryCountAndShow={validateStoryCountAndShow} keyValue={index} isLocked={!subscribedSkus.includes(item?.sku)} remainingCoreRuns={remainingCoreRuns}/>
                                        );
                                    })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {stories?.length === 0 && !loading && <div className={`${wrapperClass}__label`}>No Stories found.</div>}
        </>
    );
}

type StoriesCardProp = {
    story: ProductWithMeta;
    isLocked: boolean;
    validateStoryCountAndShow: (story: ProductWithMeta) => void;
    validateStory: (story: ProductWithMeta) => Promise<boolean>;
    keyValue: Number;
    remainingCoreRuns: Number;

};

function StoriesCard({
    story,
    isLocked,
    validateStory,
    validateStoryCountAndShow,
    keyValue,
    remainingCoreRuns,
}: StoriesCardProp) {
    const [canRun, setCanRun] = useState(true);
    const { getStoryDefaultRunConfigBySku } = useStoryRunService();
    const {company} = useUser();
    const userAccessLevel = parseInt(getAccessLevel(company?.accessLevel) ?? '0');

    async function validate() {
        const isValid = await validateStory(story);
        setCanRun(isValid);
    }
    useEffect(() => {
        validate();
    }, []);
    
    const reportDetails = {
        reportType: story?.type,
        reportSelectors: story.metadata?.env?.reportSelectors
            ? storySelectorAttributes(story?.metadata?.env?.reportSelectors)
            : {},
        productSku: story?.sku,
        storyType: story.metadata?.env?.storyType,
        reportRunValue: story.metadata?.env?.reportRunValue,
    };

    let storyConfig = { ...story, reportDetails };
    if (userAccessLevel === 5) {
                storyConfig.metadata.env.reportSelectors = storyConfig.metadata?.env?.reportSelectors.filter(
                    (e) => e !== 'market:option5'
                );
            }

    const pathParam = '/dashboard/story_builder';
    
    const searchParam = new URLSearchParams({sku: story.sku}).toString();
    const content = (
        <ByzzerCard
            key={keyValue as React.Key}
            shouldTrackClick={true}
            trackClickName={`My Reports Report (${story?.title}) clicked`}
            title={story?.title}
            onClick={canRun || !isLocked ? undefined : () => validateStoryCountAndShow(story)}
            description={story?.description}
            badges={[
                `${snakeCaseToTitleCase(story?.metadata?.reportOptions?.reportType)}`,
                `Deducts ${story?.metadata?.env?.reportRunValue} Report ${
                    story?.metadata?.env?.reportRunValue > 1 ? `Runs` : `Run`
                }`,
            ]}
            isLocked={isLocked}
            thumbnailUrl={story?.thumbnailUrl}
            thumbnailImageSize={'lg'}
        />
    );


    return canRun ? (
        <Link
            to={{ pathname: pathParam, search: searchParam }}
            state={{
                storyConfig,
                defaultValues: getStoryDefaultRunConfigBySku(storyConfig?.sku),
                remainingReportRuns: remainingCoreRuns,
            }}
            className={'my-stories__card-container'}
        >
            {content}
        </Link>
    ) : (
        <>{content}</>
    );
}

export default StoryLibrary;
